import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Instrument, Tick } from '@dabba/contracts';
import { api } from '../api/client';
import { istClock, num, pct } from '../lib/format';
import { instrumentSub, instrumentTitle } from '../lib/instrumentLabel';
import { useMarketStatus } from '../data/hooks';
import { useOrderTicket } from './OrderTicket';
import { IconStar, IconStarFilled } from './icons';

/**
 * The dealing-screen layer: the components that make a page read as an
 * instrument rather than as a dashboard. Everything here is dense on
 * purpose — a trading screen earns trust by fitting information, not by
 * spacing four numbers across a thousand pixels.
 */

/* ---------------------------------------------------------------- figures */

/**
 * A figure that paints its own cell for a beat when it changes.
 *
 * This is the one animation in the app that carries information: a glance at
 * a column tells you which rows are moving, without reading a single digit.
 * The class is swapped rather than toggled off and on so a fast sequence of
 * ticks restarts the animation instead of dropping it.
 */
export function LiveNum({
  value,
  format = (v: number) => num(v),
  className = '',
}: {
  value: number | null | undefined;
  format?: (v: number) => string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const prev = useRef<number | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (node == null || value == null) return;
    const before = prev.current;
    prev.current = value;
    if (before == null || before === value) return;
    const cls = value > before ? 'flash-up' : 'flash-down';
    node.classList.remove('flash-up', 'flash-down');
    // Forcing a reflow is what lets the same animation replay on consecutive
    // ticks; without it the class re-add is coalesced and nothing flashes.
    void node.offsetWidth;
    node.classList.add(cls);
  }, [value]);

  return (
    <span ref={ref} className={`num flash ${className}`.trim()}>
      {value == null ? '—' : format(value)}
    </span>
  );
}

/** "+12.40 (+0.88%)" in the tone of its sign — the pair read as one unit. */
export function Change({ tick, className = '' }: { tick?: Tick; className?: string }) {
  if (!tick) return <span className="num muted">—</span>;
  const tone = tick.change > 0 ? 'up' : tick.change < 0 ? 'down' : 'muted';
  return (
    <span className={`num ${tone} ${className}`.trim()}>
      {tick.change >= 0 ? '+' : '−'}
      {num(Math.abs(tick.change))} ({pct(tick.changePct)})
    </span>
  );
}

/* -------------------------------------------------------------- sparkline */

/**
 * A day's shape in 60×20 pixels.
 *
 * Candles come from the same endpoint the full chart uses, cached for five
 * minutes and never retried: a sparkline is an ornament on a row that has
 * already told you the price, so a missing one must cost nothing. When the
 * fetch fails the component renders a flat hairline rather than a gap, which
 * keeps the row's three columns aligned down the list.
 */
export function Sparkline({
  symbol,
  width = 58,
  height = 20,
  timeframe = '1D',
}: {
  symbol: string;
  width?: number;
  height?: number;
  timeframe?: string;
}) {
  const { data } = useQuery({
    queryKey: ['candles', symbol, timeframe],
    queryFn: () => api.candles(symbol, timeframe),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
  });

  const path = useMemo(() => {
    const closes = (data?.candles ?? []).map((c) => c.c);
    if (closes.length < 2) return null;
    const lo = Math.min(...closes);
    const hi = Math.max(...closes);
    const span = hi - lo || 1;
    const step = width / (closes.length - 1);
    const pad = 1.5;
    const usable = height - pad * 2;
    return {
      d: closes
        .map((c, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(pad + (1 - (c - lo) / span) * usable).toFixed(1)}`)
        .join(' '),
      rising: closes[closes.length - 1]! >= closes[0]!,
    };
  }, [data, width, height]);

  if (!path) {
    return (
      <svg width={width} height={height} className="spark" aria-hidden>
        <line
          x1="0"
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="var(--border)"
          strokeWidth="1"
        />
      </svg>
    );
  }

  return (
    <svg width={width} height={height} className="spark" aria-hidden>
      <path
        d={path.d}
        fill="none"
        stroke={path.rising ? 'var(--up)' : 'var(--down)'}
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ tiles */

export function Tiles({ children }: { children: ReactNode }) {
  return <div className="tiles">{children}</div>;
}

export function Tile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'up' | 'down' | 'muted';
}) {
  return (
    <div className="tile">
      <div className="label">{label}</div>
      <div className={`tile-value num${tone ? ` ${tone}` : ''}`}>{value}</div>
      {sub ? <div className="tile-sub">{sub}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------- section rule */

/** A small caps label with a hairline running out to the right of it. */
export function RuleHead({ label, action }: { label: string; action?: ReactNode }) {
  return (
    <div className="rule-head">
      <span className="label">{label}</span>
      {action ? <span className="rule-action">{action}</span> : null}
    </div>
  );
}

/* ------------------------------------------------------------ market clock */

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * The clock on the tape: server time while the session is on, and a live
 * countdown to the next open while it is not.
 *
 * The phase itself is always the server's — inferring "open" from the fact
 * that ticks are arriving is exactly the lie the feed indicator exists to
 * prevent. Only the countdown is arithmetic, off the server's own `nextOpen`.
 * The market/feed state is rendered next to this by the shell's FeedChip;
 * this component deliberately does not repeat it.
 */
export function MarketClock() {
  const market = useMarketStatus();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!market) return null;

  const open = market.phase === 'open';
  const until = market.nextOpen ? new Date(market.nextOpen).getTime() - now : null;
  const countdown =
    until != null && until > 0
      ? `${pad(Math.floor(until / 3_600_000))}:${pad(Math.floor(until / 60_000) % 60)}:${pad(
          Math.floor(until / 1000) % 60,
        )}`
      : null;

  return (
    <span className="tape-clock-detail label" style={{ letterSpacing: '0.08em' }}>
      {open || !countdown ? `${istClock(market.serverTimeIst)} IST` : `OPENS IN ${countdown}`}
    </span>
  );
}

/** Compact benchmark card for the strip across the top of the desk. */
export function IndexCard({
  symbol,
  tick,
  meta,
}: {
  symbol: string;
  tick?: Tick;
  meta?: Instrument;
}) {
  const change = tick?.change ?? 0;
  const tone = change > 0 ? 'up' : change < 0 ? 'down' : 'muted';
  return (
    <Link to={`/stocks/${encodeURIComponent(symbol)}`} className="ticker-card">
      <div className="label nowrap">{meta?.name ?? symbol}</div>
      <div className="between mt-1" style={{ gap: 10 }}>
        <div>
          <LiveNum value={tick?.ltp} className="t-lg semibold" />
          <div className={`num t-xs ${tone}`}>
            {tick
              ? `${change >= 0 ? '+' : '−'}${num(Math.abs(change))} (${pct(tick.changePct)})`
              : '—'}
          </div>
        </div>
        <Sparkline symbol={symbol} width={54} height={26} />
      </div>
    </Link>
  );
}

/* -------------------------------------------------------------- quote rows */

/**
 * The dense instrument row: name over segment, a day's shape, the price and
 * its change, with buy/sell revealed on hover. Three fixed columns so a list
 * of them reads as a column of prices rather than as a stack of cards.
 */
export function QuoteRow({
  symbol,
  tick,
  meta,
  watched,
  onToggleWatch,
  spark = true,
  actions = true,
}: {
  symbol: string;
  tick?: Tick;
  meta?: Instrument;
  watched?: boolean;
  onToggleWatch?: (symbol: string) => void;
  spark?: boolean;
  actions?: boolean;
}) {
  const { openTicket } = useOrderTicket();
  // Indices tick but cannot be traded; a buy button on one is a button whose
  // only outcome is a rejection.
  const tradable = meta?.kind !== 'index';

  return (
    <div className="q-row">
      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Link to={`/stocks/${encodeURIComponent(symbol)}`} style={{ minWidth: 0, flex: 1 }}>
          <div className="q-name">{instrumentTitle(symbol, meta)}</div>
          {/*
            Only rendered when it has something to say. On a list of NSE
            equities instrumentSub returns null, so the row closes up to a
            single line and the contracts — which alone keep a second line
            for their expiry and lot — become the ones that stand out. The
            company name is deliberately not a fallback here: on a dealing
            list the ticker is the identity, and the detail page carries the
            name for the one instrument you have actually opened.
          */}
          {instrumentSub(meta) ? <div className="q-sub">{instrumentSub(meta)}</div> : null}
        </Link>
        {actions && tradable ? (
          <div className="q-actions">
            <button
              className="btn btn-sm btn-buy"
              onClick={() => openTicket({ symbol, side: 'BUY' })}
              aria-label={`Buy ${symbol}`}
            >
              B
            </button>
            <button
              className="btn btn-sm btn-sell"
              onClick={() => openTicket({ symbol, side: 'SELL' })}
              aria-label={`Sell ${symbol}`}
            >
              S
            </button>
          </div>
        ) : null}
        {onToggleWatch ? (
          <button
            className="icon-btn q-star"
            onClick={() => onToggleWatch(symbol)}
            aria-label={watched ? `Remove ${symbol} from watchlist` : `Add ${symbol} to watchlist`}
          >
            {watched ? <IconStarFilled size={15} className="up" /> : <IconStar size={15} />}
          </button>
        ) : null}
      </div>

      <div className="q-spark">{spark ? <Sparkline symbol={symbol} /> : null}</div>

      <div className="right">
        <LiveNum value={tick?.ltp} className="t-md semibold" />
        <div className="t-xs">
          <Change tick={tick} />
        </div>
      </div>
    </div>
  );
}
